import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Heading,
  useToast,
  Flex,
  Text,
  Stack,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Input,
  Select,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tab,
  Tabs,
  TabList,
  TabPanel,
  TabPanels,
  Textarea,
  Spinner
} from '@chakra-ui/react';
import { ChevronDownIcon, AddIcon } from '@chakra-ui/icons';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import AddJobPage from './AddJobPage';
import useSecureApi from '../Authentication/UseSecureApi';

// New StatCard component for analytics
const StatCard = ({ label, value, color, icon }) => (
  <Box flex={1} minW="120px" p={3} bg={`${color}.50`} borderRadius="md">
    <Flex align="center" gap={2}>
      <Text fontSize="lg">{icon}</Text>
      <Text fontSize="sm" color={`${color}.600`}>{label}</Text>
    </Flex>
    <Text fontSize="2xl" fontWeight="bold" color={`${color}.800`}>
      {value}
    </Text>
  </Box>
);

// New ApplicationStats component
const ApplicationStats = ({ applications, selectedJob }) => {
  const stats = {
    total: applications.length,
    new: applications.filter(a => a.status === 'NEW').length,
    reviewed: applications.filter(a => a.status === 'REVIEWED').length,
    interview: applications.filter(a => a.status === 'INTERVIEW').length,
    rejected: applications.filter(a => a.status === 'REJECTED').length
  };

  return (
    <Box bg="white" p={4} borderRadius="md" boxShadow="sm" mb={6}>
      <Heading size="md" mb={4}>📊 Application Pipeline</Heading>
      {selectedJob && (
        <Text fontSize="sm" mb={4} color="gray.600">
          For Job #{selectedJob.id}: <strong>{selectedJob.title}</strong>
        </Text>
      )}
      <Flex gap={4} wrap="wrap">
        <StatCard label="Total" value={stats.total} color="gray" icon="👥" />
        <StatCard label="New" value={stats.new} color="blue" icon="🆕" />
        <StatCard label="Reviewed" value={stats.reviewed} color="teal" icon="👀" />
        <StatCard label="Interview" value={stats.interview} color="purple" icon="📅" />
        <StatCard label="Rejected" value={stats.rejected} color="red" icon="❌" />
      </Flex>
    </Box>
  );
};

// New StatusTrendChart component
const StatusTrendChart = ({ applications }) => {
  const data = [
    { name: 'New', count: applications.filter(a => a.status === 'NEW').length },
    { name: 'Reviewed', count: applications.filter(a => a.status === 'REVIEWED').length },
    { name: 'Interview', count: applications.filter(a => a.status === 'INTERVIEW').length },
    { name: 'Rejected', count: applications.filter(a => a.status === 'REJECTED').length }
  ];

  return (
    <Box bg="white" p={4} borderRadius="md" boxShadow="sm" mb={6}>
      <Heading size="md" mb={4}>📈 Status Trend</Heading>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data}>
          <XAxis dataKey="name" />
          <YAxis
            domain={[0, 50]}
            ticks={[...Array(51).keys()]} // generates [0, 1, 2, ..., 50]
            tickFormatter={(tick) => tick.toString()}
          />
          <Tooltip />
          <Bar dataKey="count" fill="#3182CE" />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

const JobDashboard = () => {
  const toast = useToast();
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [skillFilter, setSkillFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [jobToEdit, setJobToEdit] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingJobs, setIsFetchingJobs] = useState(true);
  const [isFetchingApplications, setIsFetchingApplications] = useState(false);

  const {
    isOpen: isJobModalOpen,
    onOpen: onJobModalOpen,
    onClose: onJobModalClose
  } = useDisclosure();

  const {
    isOpen: isEmailModalOpen,
    onOpen: onEmailModalOpen,
    onClose: onEmailModalClose
  } = useDisclosure();

  const [selectedApplication, setSelectedApplication] = useState(null);
  const [emailType, setEmailType] = useState('REJECTION');
  const api = useSecureApi();

  // Fetch jobs
  const fetchJobs = useCallback(async () => {
    setIsFetchingJobs(true);
    try {
      const { data } = await api.get('/api/jobs');
      setJobs(data || []);
    } catch (error) {
      toast({
        title: 'Error loading jobs',
        description: error.message,
        status: 'error',
      });
    } finally {
      setIsFetchingJobs(false);
    }
  }, [toast, api]); // Add api to dependencies

  // Fetch applications
  const fetchApplications = useCallback(async (jobId) => {
    setIsFetchingApplications(true);
    setApplications([]);
    try {
      const { data } = await api.get(`/api/applications/jobs/${jobId}`);
      console.log('Fetched applications:', data); // Debugging line
      setApplications(data || []);
    } catch (error) {
      setApplications([]);
      toast({
        title: 'Error loading applications',
        description: error.message,
        status: 'error',
      });
    } finally {
      setIsFetchingApplications(false);
    }
  }, [toast, api]); // Add api to dependencies

  // Load data
  useEffect(() => { fetchJobs(); }, [fetchJobs]);
  useEffect(() => {
    if (selectedJobId) fetchApplications(selectedJobId);
  }, [selectedJobId, fetchApplications]);

  // Filter applications
  const filteredApplications = applications.filter(app => {
    const matchesSkill = skillFilter
      ? app.applicantName?.toLowerCase().includes(skillFilter.toLowerCase()) ||
      app.email?.toLowerCase().includes(skillFilter.toLowerCase())
      : true;
    const matchesStatus = statusFilter ? app.status === statusFilter : true;
    return matchesSkill && matchesStatus;
  });

  // Job CRUD operations
  const handleAddOrUpdateJob = async (jobData) => {
    setIsSubmitting(true);
    try {
      if (jobToEdit) {
        const payload = { ...jobToEdit, ...jobData };
        await api.put(`/api/jobs/${jobToEdit.id}`, payload);
        toast({ title: 'Job updated', status: 'success' });
      } else {
        const payload = { ...jobData, postedDate: new Date().toISOString() };
        await api.post('/api/jobs', payload);
        toast({ title: 'Job added', status: 'success' });
      }
      setJobToEdit(null);
      await fetchJobs();
      onJobModalClose();
    } catch (error) {
      toast({
        title: 'Operation failed',
        description: error.message,
        status: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteJob = async (id) => {
    try {
      await api.delete(`/api/jobs/${id}`);
      toast({ title: 'Job deleted', status: 'info' });
      await fetchJobs();
    } catch (error) {
      toast({
        title: 'Delete failed',
        description: error.message,
        status: 'error',
      });
    }
  };

  // Application operations
  const updateApplicationStatus = async (id, newStatus) => {
    try {
      await api.patch(`/api/applications/${id}`, { status: newStatus });
      setApplications(applications.map(app =>
        app.id === id ? { ...app, status: newStatus } : app
      ));
      toast({ title: 'Status updated', status: 'success' });
    } catch (error) {
      toast({
        title: 'Update failed',
        description: error.message,
        status: 'error',
      });
    }
  };

  const sendEmail = async () => {
    if (!selectedApplication) return;
    try {
      await api.post('/api/emails/send', {
        to: selectedApplication.email,
        subject: emailType === 'INTERVIEW' ? 'Interview Invitation' : 'Application Update',
        body: emailType === 'INTERVIEW'
          ? `Dear ${selectedApplication.applicantName},...`
          : `Dear ${selectedApplication.applicantName},...`
      });
      toast({ title: 'Email sent', status: 'success' });
      onEmailModalClose();
    } catch (error) {
      toast({
        title: 'Email failed',
        description: error.message,
        status: 'error',
      });
    }
  };

  return (
    <Box p={6}>
      <Flex justify="space-between" align="center" mb={6} width="100%">
        <Heading size="lg" textAlign="center" flex="1">
          Job Management Dashboard
        </Heading>
        <Button
          leftIcon={<AddIcon />}
          colorScheme="teal"
          onClick={() => {
            setJobToEdit(null);
            onJobModalOpen();
          }}
        >
          Add Job
        </Button>
      </Flex>

      {(isFetchingJobs || isFetchingApplications) && (
        <Spinner size="xl" thickness="4px" />
      )}

      <Tabs variant="enclosed">
        <TabList>
          <Tab>Job Listings</Tab>
          <Tab isDisabled={!selectedJobId}>Applications</Tab>
        </TabList>

        <TabPanels>
          {/* Job Listings Panel */}
          <TabPanel>
            <Stack spacing={3} mt={4}>
              {jobs.map((job) => (
                <Flex
                  key={job.id}
                  borderWidth="1px"
                  borderRadius="md"
                  p={3}
                  align="center"
                  wrap="wrap"
                  gap={4}
                  boxShadow="sm"
                >
                  <Box flex="1" minW="200px">
                    <Text fontSize="lg" fontWeight="bold">{job.id}-{job.title}</Text>
                    <Text fontSize="sm" color="gray.500">{job.description}</Text>
                    <Text fontSize="sm" color="gray.500">{job.salaryRange}</Text>
                    <Text fontSize="sm" color="gray.500">
                      {job.company} | {job.type} | {job.location}
                    </Text>
                  </Box>

                  <HStack spacing={2} ml="auto">
                    <Button
                      size="sm"
                      colorScheme="blue"
                      onClick={() => setSelectedJobId(job.id)}
                    >
                      View Applications
                    </Button>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      onClick={() => {
                        setJobToEdit(job);
                        onJobModalOpen();
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      colorScheme="red"
                      onClick={() => handleDeleteJob(job.id)}
                    >
                      Delete
                    </Button>
                  </HStack>
                </Flex>
              ))}
            </Stack>
          </TabPanel>

          {/* Applications Panel */}
          <TabPanel>
            {selectedJobId && (
              <>
                <ApplicationStats applications={applications} selectedJob={jobs.find(job => job.id === selectedJobId)} />
                <StatusTrendChart applications={applications} />

                <Flex gap={4} mb={4}>
                  <Input
                    placeholder="🔍 Search candidates..."
                    value={skillFilter}
                    onChange={(e) => setSkillFilter(e.target.value)}
                    width="300px"
                  />
                  <Select
                    placeholder="Filter by status"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    width="200px"
                  >
                    <option value="NEW">New</option>
                    <option value="REVIEWED">Reviewed</option>
                    <option value="INTERVIEW">Interview</option>
                    <option value="REJECTED">Rejected</option>
                  </Select>
                </Flex>

                <Table variant="striped">
                  <Thead>
                    <Tr>
                      <Th>Candidate</Th>
                      <Th>Email</Th>
                      <Th>Phone</Th>
                      <Th>Resume</Th>
                      <Th>Status</Th>
                      <Th>Applied Date</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredApplications.map(app => (
                      <Tr key={app.id}>
                        <Td>{app.applicantName}</Td>
                        <Td>{app.email}</Td>
                        <Td>{app.phone || 'N/A'}</Td>
                        <Td>
                          {app.resumeUrl ? (
                            <a href={app.resumeUrl} target="_blank" rel="noopener noreferrer">
                              View Resume
                            </a>
                          ) : (
                            'N/A'
                          )}
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={
                              app.status === 'NEW' ? 'blue' :
                                app.status === 'REVIEWED' ? 'orange' :
                                  app.status === 'INTERVIEW' ? 'purple' : 'red'
                            }
                          >
                            {app.status}
                          </Badge>
                        </Td>
                        <Td>{new Date(app.appliedAt).toLocaleDateString()}</Td>
                        <Td>
                          <Menu>
                            <MenuButton as={Button} rightIcon={<ChevronDownIcon />} size="sm">
                              Actions
                            </MenuButton>
                            <MenuList>
                              <MenuItem onClick={() => updateApplicationStatus(app.id, 'REVIEWED')}>
                                Mark Reviewed
                              </MenuItem>
                              <MenuItem onClick={() => updateApplicationStatus(app.id, 'INTERVIEW')}>
                                Schedule Interview
                              </MenuItem>
                              <MenuItem onClick={() => updateApplicationStatus(app.id, 'REJECTED')}>
                                Reject
                              </MenuItem>
                              <MenuItem onClick={() => {
                                setSelectedApplication(app);
                                setEmailType('INTERVIEW');
                                onEmailModalOpen();
                              }}>
                                Send Interview Email
                              </MenuItem>
                              <MenuItem onClick={() => {
                                setSelectedApplication(app);
                                setEmailType('REJECTION');
                                onEmailModalOpen();
                              }}>
                                Send Rejection Email
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Add/Edit Job Modal */}
      <Modal isOpen={isJobModalOpen} onClose={onJobModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{jobToEdit ? 'Edit Job' : 'Add Job'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <AddJobPage
              jobToEdit={jobToEdit}
              onSubmit={handleAddOrUpdateJob}
              isLoading={isSubmitting}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Email Modal */}
      <Modal isOpen={isEmailModalOpen} onClose={onEmailModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {emailType === 'INTERVIEW' ? 'Interview Invite' : 'Rejection Email'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Textarea defaultValue={
              emailType === 'INTERVIEW'
                ? `Dear ${selectedApplication?.applicantName},\n\nWe are pleased to invite you...`
                : `Dear ${selectedApplication?.applicantName},\n\nThank you for applying...`
            } />
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={sendEmail}>
              Send
            </Button>
            <Button variant="ghost" onClick={onEmailModalClose}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default JobDashboard;
